import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VisasManagement = () => {
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVisa, setEditingVisa] = useState(null);
  const [formData, setFormData] = useState({
    visa_type: '',
    validity_days: 30,
    includes_siskopatuh: false,
    price: 0
  });

    const handleToggleActive = async (itemId, currentStatus) => {
    try {
      await axios.put(`${API}/master/visas/${itemId}`, { is_active: !currentStatus });
      toast.success('Status berhasil diubah');
      fetchVisas();
    } catch (error) {
      toast.error('Gagal mengubah status');
    }
  };

const fetchVisas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/master/visas?show_all=true`);
      setVisas(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching visas:', error);
      }
      toast.error('Gagal memuat data visa');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVisas();
  }, [fetchVisas]);

  const handleOpenModal = (visa = null) => {
    if (visa) {
      setEditingVisa(visa);
      setFormData({
        visa_type: visa.visa_type || '',
        validity_days: visa.validity_days || 30,
        includes_siskopatuh: visa.includes_siskopatuh || false,
        price: visa.price || 0
      });
    } else {
      setEditingVisa(null);
      setFormData({
        visa_type: '',
        validity_days: 30,
        includes_siskopatuh: false,
        price: 0
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      validity_days: parseInt(formData.validity_days),
      price: parseFloat(formData.price)
    };

    try {
      if (editingVisa) {
        await axios.put(`${API}/master/visas/${editingVisa.id}`, payload);
        toast.success('Visa berhasil diupdate');
      } else {
        await axios.post(`${API}/master/visas`, payload);
        toast.success('Visa berhasil ditambahkan');
      }
      setShowModal(false);
      fetchVisas();
    } catch (error) {
      toast.error(editingVisa ? 'Gagal update visa' : 'Gagal menambah visa');
    }
  };

  const handleDelete = async (visaId) => {
    if (!window.confirm('Yakin ingin menghapus visa ini?')) return;

    try {
      await axios.delete(`${API}/master/visas/${visaId}`);
      toast.success('Visa berhasil dihapus');
      fetchVisas();
    } catch (error) {
      toast.error('Gagal menghapus visa');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Memuat data...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-['Outfit']">Visa Management</h1>
        <Button className="rounded-full" onClick={() => handleOpenModal()}>
          <Plus className="h-4 w-4 mr-2" />
          Tambah Visa
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>Daftar Visa</CardTitle>
        </CardHeader>
        <CardContent>
          {visas.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Belum ada data visa
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-semibold">Jenis Visa</th>
                    <th className="text-left p-4 font-semibold">Masa Berlaku</th>
                    <th className="text-left p-4 font-semibold">Termasuk Siskopatuh</th>
                    <th className="text-left p-4 font-semibold">Harga</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-right p-4 font-semibold">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {visas.map((visa) => (
                    <tr key={visa.id} className="border-b hover:bg-muted/50">
                      <td className="p-4 font-medium">{visa.visa_type}</td>
                      <td className="p-4 text-muted-foreground">
                        {visa.validity_days} hari
                      </td>
                      <td className="p-4">
                        <Badge variant={visa.includes_siskopatuh ? 'default' : 'secondary'}>
                          {visa.includes_siskopatuh ? 'Ya' : 'Tidak'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        Rp {visa.price?.toLocaleString('id-ID')}
                      </td>
                      <td className="p-4">
                        <Badge variant={visa.is_active ? 'default' : 'secondary'}>
                          {visa.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleToggleActive(visa.id, visa.is_active)}
                          >
                            {visa.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleOpenModal(visa)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => handleDelete(visa.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingVisa ? 'Edit Visa' : 'Tambah Visa Baru'}
            </DialogTitle>
            <DialogDescription>
              Lengkapi form di bawah untuk {editingVisa ? 'mengupdate' : 'menambahkan'} visa
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="visa_type">Jenis Visa *</Label>
                <Input
                  id="visa_type"
                  value={formData.visa_type}
                  onChange={(e) => setFormData({...formData, visa_type: e.target.value})}
                  required
                  placeholder="Visa Umrah"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="validity_days">Masa Berlaku (hari) *</Label>
                  <Input
                    id="validity_days"
                    type="number"
                    value={formData.validity_days}
                    onChange={(e) => setFormData({...formData, validity_days: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Harga *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="includes_siskopatuh"
                  checked={formData.includes_siskopatuh}
                  onChange={(e) => setFormData({...formData, includes_siskopatuh: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="includes_siskopatuh" className="cursor-pointer">
                  Termasuk Siskopatuh
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Batal
              </Button>
              <Button type="submit">
                {editingVisa ? 'Update' : 'Tambah'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisasManagement;